/**
 * Auth Store (Zustand) — single source of truth for authentication state.
 *
 * Replaces the former AuthContext wrapper. The matching initialization +
 * Supabase subscription + inactivity-timer side-effects live in
 * `src/hooks/useAuthInit.ts` and must be mounted once at the app root.
 *
 * Credentials (auth migration, phase 5b-2)
 * ----------------------------------------
 * The Supabase session is the app's credential: login() signs in to Supabase
 * Auth, services/apiClient sends the session as Bearer on every backend
 * request, and useAuthInit restores the user from it on reload. The legacy
 * HttpOnly cookie from POST /auth/login is acquired only by the legacy
 * fallback in login() — the path for an account whose password Supabase
 * rejects — and is what apiClient's cookie-only 401 retry falls back on.
 */

import { create } from 'zustand';
import { authAPI, setCsrfToken, setAuthBootstrap } from '../services/api';
import { supabase } from '../supabaseClient';
import { ensureSupabaseSession, clearSupabaseSession } from '../lib/supabaseSession';
import { fetchUserProfileDirect } from '../lib/userProfileDirect';
import logger from '../utils/logger';
import type { User } from '../types/models';

export type AuthMethod = 'legacy' | 'supabase' | null;

export interface LoginResult {
  success: true;
  /**
   * Which route got the user in:
   *   'direct'   — Supabase Auth + a direct profile read; no backend call.
   *   'legacy'   — POST /auth/login, awaited (Supabase rejected the
   *                credentials, or the profile row could not be read).
   *   'supabase' — Supabase Auth + /auth/me-v2, awaited.
   */
  method: 'legacy' | 'supabase' | 'direct';
}

interface AuthState {
  user: User | null;
  loading: boolean;
  authMethod: AuthMethod;
}

interface AuthActions {
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setAuthMethod: (method: AuthMethod) => void;
  setAuthState: (state: Partial<AuthState>) => void;
  clearAuth: () => void;

  /** Sign in to Supabase; fall back to the legacy login when it rejects. */
  login: (email: string, password: string) => Promise<LoginResult>;
  /** Sign out: store + Supabase session first, then CSRF token + cookie. */
  logout: () => Promise<void>;
  /** Register a new user via Supabase Auth. */
  register: (userData: Record<string, unknown>) => Promise<{ success: true; user: User }>;
}

type AuthStore = AuthState & AuthActions;

let signInInFlight = false;

/**
 * True while login() is driving a Supabase sign-in of its own.
 *
 * That sign-in raises SIGNED_IN, and useAuthInit answers SIGNED_IN by fetching
 * the profile from /auth/me-v2 — the backend round-trip this whole path exists
 * to avoid, for a profile login() is already setting from the same row. The
 * subscription callback has to read this synchronously and nothing renders
 * from it, so it is a plain module flag rather than store state.
 */
export function isStoreSignInInFlight(): boolean {
  return signInInFlight;
}

/**
 * Sign in to Supabase, returning the session or null.
 *
 * Null covers both "Supabase is not configured" and "Supabase rejected these
 * credentials" — an account that predates the backfill, or a password only
 * ever changed on the legacy side. Both mean the same thing to the caller:
 * fall back to the legacy login.
 */
async function signInSupabase(email: string, password: string) {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error || !data?.session) {
      if (error) logger.warn('Supabase sign-in rejected; using legacy login', error.message);
      return null;
    }
    return data.session;
  } catch (error) {
    logger.warn('Supabase sign-in failed; using legacy login', error);
    return null;
  }
}

export const useAuthStore = create<AuthStore>((set) => ({
  // State
  user: null,
  loading: true,
  authMethod: null,

  // Setters
  setUser: (user) => set({ user }),
  setLoading: (loading) => set({ loading }),
  setAuthMethod: (authMethod) => set({ authMethod }),
  setAuthState: (state) => set(state),
  clearAuth: () => set({ user: null, authMethod: null }),

  login: async (email, password) => {
    // Supabase Auth never sleeps; the backend on Render's free tier does, and
    // a cold start costs ~54s. Every account has a matching Supabase Auth user
    // with the same hash, so signing in there and reading public.users under
    // the session's own RLS answers "who is this" in well under a second —
    // and the session it leaves behind is the credential apiClient sends as
    // Bearer, so the backend is not involved in this path at all.
    signInInFlight = true;
    try {
      const session = await signInSupabase(email, password);

      if (session) {
        const profile = await fetchUserProfileDirect(session.user.id);
        if (profile) {
          // The Supabase session is the credential from here on: the backend
          // accepts it as Bearer on every route (5a), apiClient attaches it
          // (5b-1), and useAuthInit restores from it on reload. No legacy
          // cookie is acquired and no bootstrap gate is set — the gate only
          // ever waited for that cookie, and a never-set gate holds nothing.
          set({ user: profile, authMethod: 'supabase' });
          return { success: true, method: 'direct' };
        }
        // The session is good but the row could not be read directly (an
        // account with no auth_user_id link, or RLS returning nothing). Fall
        // through: the backend still resolves the same profile, just slowly.
      }

      // Legacy fallback, unchanged — the path for credentials Supabase
      // rejects (a password that only lives in the legacy store). This is
      // the one place the legacy cookie is still acquired; apiClient's
      // cookie-only 401 retry exists for sessions that got in this way.
      try {
        const res = await authAPI.login(email, password);
        const payload = res.data as {
          access_token: string;
          user: User;
          corporate_group_id?: number;
        };
        // Issue #683: access_token no longer stored in localStorage.
        // The backend sets an HttpOnly `access_token` cookie on this
        // login response; subsequent API calls rely on that cookie.
        const userData: User = { ...payload.user };
        if (payload.corporate_group_id) {
          userData.corporate_group_id = payload.corporate_group_id;
        }
        set({ user: userData, authMethod: 'legacy' });
        // Give the browser a Supabase session too, so RLS can resolve
        // auth.uid() and screens may read Supabase directly instead of
        // routing every query through the backend. Skipped when the sign-in
        // above already opened one. Deliberately not awaited: the user is
        // already logged in, and a failure here only costs the direct path.
        if (!session) void ensureSupabaseSession(email, password);
        return { success: true, method: 'legacy' };
      } catch (legacyError) {
        // Supabase accepted the credentials but neither the direct read nor
        // the legacy login produced a profile. /auth/me-v2 resolves one
        // server-side from the Supabase JWT — slow, but it is the difference
        // between signing in and not.
        if (session) {
          try {
            const res = await authAPI.getCurrentUserV2();
            if (res.status === 200) {
              set({ user: res.data as User, authMethod: 'supabase' });
              return { success: true, method: 'supabase' };
            }
          } catch (supabaseError) {
            logger.error('Supabase login fallback failed:', supabaseError);
          }
        }
        throw new Error('Invalid email or password');
      }
    } finally {
      signInInFlight = false;
    }
  },

  logout: async () => {
    // #878: after logout, a reload signed the user straight back in. This
    // used to await /auth/logout before touching the Supabase session, and
    // on Render's free tier that await can last a cold start (~50s); a reload
    // inside that window found the session still in localStorage, and
    // useAuthInit restores from it without asking the backend. So the local
    // state goes first, and nothing user-visible waits on the backend.
    set({ user: null, authMethod: null });
    // Every login path opens a Supabase session (the legacy fallback adds one
    // afterwards), so this is unconditional rather than keyed on authMethod.
    // Awaited: the session must be out of localStorage before anything
    // slower runs. Supabase Auth has no cold start — see lib/supabaseSession.
    await clearSupabaseSession();
    // Nothing in the store sets the bootstrap gate any more (the direct login
    // stopped acquiring the cookie), but dropping it here keeps a stale gate
    // from ever outliving a session.
    setAuthBootstrap(null);
    // Drop the cached CSRF token so the next login negotiates a fresh pair
    // instead of trying to reuse the old one cross-origin. Safe to do before
    // /auth/logout: the backend exempts it from CSRF (cookie_auth.py).
    setCsrfToken(null);
    // The HttpOnly cookie exists only after a legacy-fallback login, and only
    // the backend can clear it. Still awaited so that cookie is gone by the
    // time this resolves, but best-effort: the user is already signed out
    // whatever the backend answers, or however long it takes to wake.
    try {
      await authAPI.logout();
    } catch (error) {
      logger.warn('Backend logout failed; local sign-out already complete', error);
    }
  },

  register: async (userData) => {
    const res = await authAPI.registerV2(userData);
    const payload = res.data as { access_token?: string; user: User };
    if (!payload.access_token) {
      throw new Error('Registration did not return a session token');
    }
    set({ user: payload.user, authMethod: 'supabase' });
    return { success: true, user: payload.user };
  },
}));
